/**
 * Floating panel window manager: panels can be moved (with edge snapping),
 * resized, collapsed, closed and re-opened. The layout persists in
 * localStorage and adapts when the browser window is resized.
 */
import { signal } from '@preact/signals';
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Icon } from './icons';

export interface PanelRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PanelState extends PanelRect {
  z: number;
  open: boolean;
  collapsed: boolean;
}

export interface PanelDef {
  id: string;
  title: string;
  help: string;
  minW: number;
  minH: number;
  render: () => JSX.Element;
  /** Optional extra controls in the title bar. */
  header?: () => JSX.Element | null;
}

const STORAGE_KEY = 'aperto.layout.v2';
const SNAP = 8;
const TITLE_H = 30;

export const layout = signal<Record<string, PanelState>>({});
export const workspaceSize = signal({ w: 1600, h: 900 });
let zTop = 10;

/** Default arrangement for a workspace of size W x H. */
export function defaultLayout(W: number, H: number): Record<string, PanelState> {
  const g = 8;
  const leftW = Math.round(W * 0.57);
  const imgH = Math.round(H * 0.6);
  const colX = leftW + 2 * g;
  const colW = Math.floor((W - colX - 2 * g) / 2);
  const col2X = colX + colW + g;
  const r = (x: number, y: number, w: number, h: number, open = true, z = 1): PanelState => ({ x, y, w, h, z, open, collapsed: false });
  const c1a = Math.round(H * 0.34);
  const c1b = Math.round(H * 0.36);
  const c2a = Math.round(H * 0.46);
  const c2b = Math.round(H * 0.27);
  return {
    image: r(g, g, leftW, imgH),
    profile: r(g, imgH + 2 * g, leftW, H - imgH - 3 * g),
    sources: r(colX, g, colW, c1a),
    photometry: r(colX, c1a + 2 * g, colW, c1b),
    display: r(colX, c1a + c1b + 3 * g, colW, H - c1a - c1b - 4 * g),
    metrics: r(col2X, g, colW, c2a),
    rois: r(col2X, c2a + 2 * g, colW, c2b),
    inspector: r(col2X, c2a + c2b + 3 * g, colW, H - c2a - c2b - 4 * g),
    calibration: r(Math.round(W * 0.3), Math.round(H * 0.12), 440, Math.round(H * 0.7), false, 5),
    analysis: r(Math.round(W * 0.2), Math.round(H * 0.15), 720, Math.round(H * 0.62), false, 5),
    export: r(Math.round(W * 0.35), Math.round(H * 0.15), 400, Math.round(H * 0.6), false, 5),
    help: r(Math.round(W * 0.25), Math.round(H * 0.08), 620, Math.round(H * 0.8), false, 6),
  };
}

export function loadLayout(W: number, H: number) {
  const def = defaultLayout(W, H);
  let saved: Record<string, PanelState> = {};
  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    saved = {};
  }
  const merged: Record<string, PanelState> = {};
  // Accept only well-typed saved values (the stored layout may be stale or corrupted).
  const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  for (const id of Object.keys(def)) {
    const sv = (saved && typeof saved === 'object' ? saved[id] : undefined) as Partial<PanelState> | undefined;
    const d = def[id];
    merged[id] = sv
      ? {
          x: num(sv.x, d.x),
          y: num(sv.y, d.y),
          w: Math.max(80, num(sv.w, d.w)),
          h: Math.max(TITLE_H, num(sv.h, d.h)),
          z: num(sv.z, d.z),
          open: typeof sv.open === 'boolean' ? sv.open : d.open,
          collapsed: typeof sv.collapsed === 'boolean' ? sv.collapsed : d.collapsed,
        }
      : d;
  }
  zTop = Math.max(10, ...Object.values(merged).map((p) => p.z)) + 1;
  layout.value = clampAll(merged, W, H);
}

let saveTimer = 0;
function persist() {
  clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layout.value));
    } catch {
      /* storage unavailable */
    }
  }, 300);
}

export function resetLayout() {
  const { w, h } = workspaceSize.value;
  layout.value = defaultLayout(w, h);
  persist();
}

function clampAll(l: Record<string, PanelState>, W: number, H: number) {
  const out: Record<string, PanelState> = {};
  for (const [id, p] of Object.entries(l)) {
    const w = Math.min(p.w, W);
    const h = Math.min(p.h, H);
    out[id] = { ...p, w, h, x: Math.max(0, Math.min(p.x, W - Math.min(w, 120))), y: Math.max(0, Math.min(p.y, H - TITLE_H)) };
  }
  return out;
}

export function setPanel(id: string, patch: Partial<PanelState>) {
  layout.value = { ...layout.value, [id]: { ...layout.value[id], ...patch } };
  persist();
}

export function openPanel(id: string, open = true) {
  setPanel(id, { open, collapsed: false, z: ++zTop });
}

export function togglePanel(id: string) {
  const p = layout.value[id];
  if (!p) return;
  if (p.open && p.z < zTop) openPanel(id);
  else setPanel(id, { open: !p.open, z: ++zTop });
}

function front(id: string) {
  if (layout.value[id]?.z === zTop) return;
  setPanel(id, { z: ++zTop });
}

/** Snap a coordinate to any candidate edge within SNAP px. */
function snap(v: number, edges: number[]): number {
  let best = v;
  let d = SNAP + 1;
  for (const e of edges) {
    const dd = Math.abs(v - e);
    if (dd < d) {
      d = dd;
      best = e;
    }
  }
  return best;
}

function edgesFor(id: string, W: number, H: number) {
  const xs = [0, W];
  const ys = [0, H];
  for (const [k, p] of Object.entries(layout.value)) {
    if (k === id || !p.open) continue;
    const h = p.collapsed ? TITLE_H : p.h;
    xs.push(p.x, p.x + p.w, p.x - 8, p.x + p.w + 8);
    ys.push(p.y, p.y + h, p.y - 8, p.y + h + 8);
  }
  return { xs, ys };
}

type DragKind = 'move' | 'r' | 'b' | 'rb' | 'l' | 'lb';

function Panel({ def, st }: { def: PanelDef; st: PanelState }) {
  const drag = useRef<{ kind: DragKind; sx: number; sy: number; r0: PanelRect } | null>(null);

  const start = (kind: DragKind) => (e: PointerEvent) => {
    if (e.button !== 0) return;
    if (kind === 'move' && (e.target as HTMLElement).closest('button, input, select')) return;
    e.preventDefault();
    front(def.id);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { kind, sx: e.clientX, sy: e.clientY, r0: { x: st.x, y: st.y, w: st.w, h: st.h } };
    document.body.classList.add('wm-dragging');
  };
  const move = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const { w: W, h: H } = workspaceSize.value;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    const { xs, ys } = edgesFor(def.id, W, H);
    const r = { ...d.r0 };
    if (d.kind === 'move') {
      let x = d.r0.x + dx;
      let y = d.r0.y + dy;
      const sx1 = snap(x, xs);
      const sx2 = snap(x + r.w, xs) - r.w;
      x = Math.abs(sx1 - x) <= Math.abs(sx2 - x) ? sx1 : sx2;
      const sy1 = snap(y, ys);
      const hh = st.collapsed ? TITLE_H : r.h;
      const sy2 = snap(y + hh, ys) - hh;
      y = Math.abs(sy1 - y) <= Math.abs(sy2 - y) ? sy1 : sy2;
      r.x = Math.max(-r.w + 80, Math.min(W - 80, x));
      r.y = Math.max(0, Math.min(H - TITLE_H, y));
    } else {
      if (d.kind.includes('r')) r.w = Math.max(def.minW, snap(d.r0.x + d.r0.w + dx, xs) - d.r0.x);
      if (d.kind.includes('l')) {
        const nx = Math.min(d.r0.x + d.r0.w - def.minW, snap(d.r0.x + dx, xs));
        r.w = d.r0.x + d.r0.w - nx;
        r.x = nx;
      }
      if (d.kind.includes('b')) r.h = Math.max(def.minH, snap(d.r0.y + d.r0.h + dy, ys) - d.r0.y);
    }
    setPanel(def.id, r);
  };
  const end = () => {
    drag.current = null;
    document.body.classList.remove('wm-dragging');
  };
  const handlers = (k: DragKind) => ({ onPointerDown: start(k), onPointerMove: move, onPointerUp: end, onPointerCancel: end });

  if (!st.open) return null;
  return (
    <section
      class={`panel${st.collapsed ? ' collapsed' : ''}`}
      style={{ left: `${st.x}px`, top: `${st.y}px`, width: `${st.w}px`, height: st.collapsed ? `${TITLE_H}px` : `${st.h}px`, zIndex: st.z }}
      onPointerDownCapture={() => front(def.id)}
      data-panel={def.id}
      aria-label={def.title}
    >
      <header class="panel-title" {...handlers('move')} onDblClick={() => setPanel(def.id, { collapsed: !st.collapsed })}>
        <span class="panel-name" data-help={def.help}>
          {def.title}
        </span>
        <span class="panel-header-extra">{def.header?.()}</span>
        <button
          type="button"
          class="panel-btn"
          data-help="panel.collapse"
          aria-label={st.collapsed ? 'Expand panel' : 'Collapse panel'}
          onClick={() => setPanel(def.id, { collapsed: !st.collapsed })}
        >
          <Icon name={st.collapsed ? 'chevronRight' : 'chevronDown'} size={14} />
        </button>
        <button
          type="button"
          class="panel-btn"
          data-help="panel.close"
          aria-label="Close panel"
          onClick={() => setPanel(def.id, { open: false })}
        >
          <Icon name="close" size={14} />
        </button>
      </header>
      {!st.collapsed && (
        <>
          <div class="panel-body">{def.render()}</div>
          <div class="rz rz-r" {...handlers('r')} />
          <div class="rz rz-b" {...handlers('b')} />
          <div class="rz rz-rb" {...handlers('rb')} />
          <div class="rz rz-l" {...handlers('l')} />
          <div class="rz rz-lb" {...handlers('lb')} />
        </>
      )}
    </section>
  );
}

export function WindowManager({ panels, children }: { panels: PanelDef[]; children?: ComponentChildren }) {
  const ws = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ws.current!;
    const measure = (first: boolean) => {
      const W = el.clientWidth;
      const H = el.clientHeight;
      const prev = workspaceSize.value;
      workspaceSize.value = { w: W, h: H };
      if (first) loadLayout(W, H);
      else if (prev.w !== W || prev.h !== H) {
        // Scale positions proportionally so the arrangement survives window resizes.
        const kx = W / prev.w;
        const ky = H / prev.h;
        const l: Record<string, PanelState> = {};
        for (const [id, p] of Object.entries(layout.value)) l[id] = { ...p, x: p.x * kx, y: p.y * ky, w: p.w * kx, h: p.h * ky };
        layout.value = clampAll(l, W, H);
        persist();
      }
    };
    measure(true);
    const ro = new ResizeObserver(() => measure(false));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const l = layout.value;
  return (
    <div class="workspace" ref={ws}>
      {panels.map((p) => (l[p.id] ? <Panel key={p.id} def={p} st={l[p.id]} /> : null))}
      {children}
    </div>
  );
}
